import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate } from '../../utils/format';
import { peakOf, percent } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge
} from '../../components/ui';
import { pastoralSummary, requestStage } from './analytics';
import type { VisitRequestLike, PastoralVisitLike, CounselingLike, SickLogLike } from './analytics';

export interface PastoralSummaryProps {
  requests: VisitRequestLike[];
  visits: PastoralVisitLike[];
  sessions: CounselingLike[];
  sick: SickLogLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

/** "Overdue 3 d", "Due today", "Due in 4 d", or "No follow-up set". */
const dueLabel = (dueIn: number | null) =>
  (dueIn === null ? 'No follow-up set' : dueIn < 0 ? `Overdue ${-dueIn} d` : dueIn === 0 ? 'Due today' : `Due in ${dueIn} d`);

/** The Pastoral Care module's Summary tab: tiles, then report panels and watch-lists. */
const PastoralSummaryView = ({ requests, visits, sessions, sick, loading, isDarkMode }: PastoralSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => pastoralSummary(requests, visits, sessions, sick), [requests, visits, sessions, sick]);
  const typePeak = peakOf(s.requestsByType, (r) => r.count);
  const stagePeak = peakOf(s.requestsByStage, (r) => r.count);
  const monthPeak = peakOf(s.visitMonths, (r) => r.count);
  const sessionStatusPeak = peakOf(s.sessionsByStatus, (r) => r.count);
  const pastorPeak = peakOf(s.byPastor, (r) => r.total);
  const stageColor = (name: string) =>
    (name === 'Completed' ? t.income : name === 'Cancelled' ? t.textFaint : name === 'Pending' ? t.warning : t.info);

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Open Requests" value={String(s.openRequests.length)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Awaiting a Pastor" value={String(s.unassigned)} color={s.unassigned ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Visits This Month" value={String(s.visitsThisMonth)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Visits This Year" value={String(s.visitsThisYear)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Counseling This Month" value={String(s.sessionsThisMonth)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Counseling Ongoing" value={String(s.ongoingSessions)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Sick Under Care" value={String(s.underCare)} color={s.underCare ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Follow-ups Due (7 d)" value={String(s.followUpsDueSoon)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Follow-ups Overdue" value={String(s.followUpsOverdue)} color={s.followUpsOverdue ? t.danger : t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Requests by Type"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Request Type', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={typePeak} color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Requests', align: 'right' }
          ]}
          rows={s.requestsByType}
          rowKey="name"
          emptyMessage="No visit requests yet."
          below={s.requestTotal ? <TotalRow label="Total requests" value={s.requestTotal} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Requests by Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Status', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={stagePeak} color={stageColor(r.name)} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Requests', align: 'right' }
          ]}
          rows={s.requestsByStage}
          rowKey="name"
          emptyMessage="No visit requests yet."
        />
      </SummaryColumns>

      <TablePanel
        title="Visits Done by Month"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="420px"
        columns={[
          { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
          { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={monthPeak} color={t.income} isDarkMode={isDarkMode} /> },
          { key: 'count', header: 'Visits', align: 'right' }
        ]}
        rows={s.visitMonths}
        rowKey="month"
        emptyMessage="No visits recorded yet."
        below={<TotalRow label="Total, last 12 months" value={s.visitMonths.reduce((n, m) => n + m.count, 0)} isDarkMode={isDarkMode} />}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Counseling by Type"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Type', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.count, s.sessionTotal)} max={100} showPercent color={t.info} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Sessions', align: 'right' }
          ]}
          rows={s.sessionsByType}
          rowKey="name"
          emptyMessage="No counseling sessions yet."
          below={s.sessionTotal ? <TotalRow label="Total sessions" value={s.sessionTotal} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Counseling by Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Status', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={sessionStatusPeak} color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Sessions', align: 'right' }
          ]}
          rows={s.sessionsByStatus}
          rowKey="name"
          emptyMessage="No counseling sessions yet."
        />
      </SummaryColumns>

      <TablePanel
        title="Care by Pastor"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'name', header: 'Pastor', render: (r) => <strong>{r.name}</strong> },
          { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={pastorPeak} color={t.primary} isDarkMode={isDarkMode} /> },
          { key: 'visits', header: 'Visits', align: 'right' },
          { key: 'counseling', header: 'Counseling', align: 'right' },
          { key: 'sick', header: 'Sick Calls', align: 'right' },
          { key: 'openRequests', header: 'Open Requests', align: 'right' }
        ]}
        rows={s.byPastor}
        rowKey="name"
        emptyMessage="No pastoral care recorded yet."
      />

      <TablePanel
        title="Sick Members — Follow-ups Needing Attention"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'memberName', header: 'Member', render: (r) => <strong>{r.memberName || '-'}</strong> },
          { key: 'location', header: 'Where', wrap: true, render: (r) => r.location || '-' },
          { key: 'pastorName', header: 'Pastor', render: (r) => r.pastorName || '-' },
          { key: 'followUpDate', header: 'Follow-up', nowrap: true, render: (r) => (r.followUpDate ? formatDate(r.followUpDate) : '-') },
          {
            key: 'dueIn',
            header: 'Status',
            nowrap: true,
            render: (r) => <Badge tone={r.dueIn === null ? 'neutral' : r.dueIn < 0 ? 'error' : 'warning'}>{dueLabel(r.dueIn)}</Badge>
          }
        ]}
        rows={s.sickWatch}
        rowKey={(r) => `${r.recordId}|${r.memberName}|${r.date}`}
        emptyMessage="No follow-ups are due or overdue."
      />

      <TablePanel
        title="Open Visit Requests"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'date', header: 'Preferred Date', nowrap: true, render: (r) => <strong>{formatDate(r.preferredDate || r.date)}</strong> },
          { key: 'requestedBy', header: 'Requested By', render: (r) => r.requestedBy || r.memberName || '-' },
          { key: 'requestType', header: 'Type', render: (r) => r.requestType || r.purpose || '-' },
          { key: 'assignedPastor', header: 'Pastor', render: (r) => (r.assignedPastor ? r.assignedPastor : <Badge tone="warning">Not assigned</Badge>) },
          { key: 'status', header: 'Status', render: (r) => <Badge tone="neutral">{requestStage(r)}</Badge> }
        ]}
        rows={s.openRequests}
        rowKey={(r) => `${r.recordId}|${r.requestedBy}|${r.preferredDate}`}
        emptyMessage="Every visit request has been dealt with."
      />
    </>
  );
};

export default PastoralSummaryView;
